// src/components/DiffsonaView.ts
import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getDiffsona, retrainDiffsona, getGeneratedContent } from '../client/ApiClient';
import { CDN_CONTENT_ENDPOINT } from '../constants';

type Diffsona = {
  name: string;
  slug: string;
  primary_media_ids: string[];
  secondary_media_ids: string[];
};

@customElement('diffsona-view')
export class DiffsonaView extends LitElement {
  createRenderRoot() { return this; }

  @state() private slug = '';
  @state() private d: Diffsona | null = null;

  @state() private loading = true;
  @state() private working = false;
  @state() private error: string | null = null;

  @state() private activeTab: 'source' | 'content' = 'content'; // default to the fun one ✨
  @state() private showUpload = false;

  // Generated content
  @state() private loadingContent = false;
  @state() private images: string[] = []; // full CDN URLs

  // Share modal state (gallery)
  @state() private shareOpen = false;
  @state() private shareIdx = 0;

  connectedCallback() {
    super.connectedCallback();
    const m = location.pathname.match(/^\/diffsona\/([^/]+)/);
    if (m?.[1]) {
      this.slug = decodeURIComponent(m[1]);
      this.loadDiffsona();
    }
  }

  private async loadDiffsona() {
    this.loading = true; this.error = null;
    try {
      const data = await getDiffsona(this.slug);
      this.d = data;
      if (this.activeTab === 'content') {
        this.loadGenerated(); // auto load
      }
    } catch (e: any) {
      this.error = e?.message || 'Failed to load';
    } finally {
      this.loading = false;
    }
  }

  @state() private nextCursor: string | null | undefined = null;
  @state() private loadingMore = false;
  private io?: IntersectionObserver;
  private sentinel?: HTMLElement;

  private async loadGenerated(initial = false) {
    if (initial) {
      this.images = [];
      this.nextCursor = null;
    }
    this.loadingContent = true;
    try {
      const page = await getGeneratedContent(this.slug, {
        limit: 24,
        cursor: initial ? undefined : this.nextCursor || undefined,
      });
      const newUrls = (page.image_urls || []).map(u => CDN_CONTENT_ENDPOINT + u);
      this.images = [...this.images, ...newUrls];
      this.nextCursor = page.next_cursor || null;
    } catch (e) {
      console.error('Failed to load generated content:', (e as any)?.message);
    } finally {
      this.loadingContent = false;
    }
  }

  firstUpdated() {
    // Set up sentinel observer for infinite scroll
    this.io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) this.loadMore();
      }
    }, { root: null, rootMargin: '400px 0px', threshold: 0 });

    this.sentinel = this.querySelector('#gc-sentinel') as HTMLElement;
    if (this.sentinel) this.io.observe(this.sentinel);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.io && this.sentinel) this.io.unobserve(this.sentinel);
    this.io = undefined;
    this.sentinel = undefined;
  }

  private async loadMore() {
    if (this.loadingMore) return;
    if (!this.nextCursor) return; // no more
    this.loadingMore = true;
    try {
      const page = await getGeneratedContent(this.slug, { limit: 24, cursor: this.nextCursor });
      const newUrls = (page.image_urls || []).map(u => CDN_CONTENT_ENDPOINT + u);
      this.images = [...this.images, ...newUrls];
      this.nextCursor = page.next_cursor || null;
    } catch (e) {
      console.error('loadMore failed:', (e as any)?.message);
    } finally {
      this.loadingMore = false;
    }
  }

  // --- Actions ---
  private async onRetrain() {
    if (this.working) return;
    this.working = true;
    try {
      await retrainDiffsona(this.slug);
      alert('Retraining scheduled!');
    } catch (e: any) {
      alert('Failed to retrain: ' + (e?.message || 'Unknown error'));
    } finally {
      this.working = false;
    }
  }

  private closeShare = () => { this.shareOpen = false; };

  // --- UI helpers ---
  private tab(label: string, key: 'source' | 'content') {
    const active = this.activeTab === key;
    return html`
      <button
        class="rounded-xl px-3 py-1.5 text-sm font-medium border transition
               ${active
        ? 'border-indigo-400/30 bg-indigo-500/20'
        : 'border-white/10 bg-white/5 hover:bg-white/10'}"
        @click=${() => {
        this.activeTab = key;
        if (key === 'content' && !this.images.length) this.loadGenerated();
      }}
      >${label}</button>
    `;
  }

  private skeletonCard() {
    return html`
      <div class="rounded-2xl border border-white/10 bg-slate-900/40 p-2">
        <div class="h-72 md:h-80 w-full rounded-xl bg-slate-800/80 animate-pulse"></div>
      </div>
    `;
  }

  // Add this helper inside the class:
  private makeFetcher() {
    return async (cursor?: string) => {
      const page = await getGeneratedContent(this.slug, { limit: 24, cursor });
      const items = (page.image_urls || []).map(u => CDN_CONTENT_ENDPOINT + u);
      return { items, nextCursor: page.next_cursor ?? null };
    };
  }

  // + New state
  @state() private gallery: string[] = [];
  @state() private galleryIndex = 0;

  // + Open from grid: trust the grid’s ordering
  private openFromGrid = (e: CustomEvent) => {
    const { index, items } = e.detail as { index: number; items: string[] };
    this.gallery = items || [];
    this.galleryIndex = index ?? 0;
    this.shareOpen = true;
  };

  // + Modal navigation now uses gallery[]
  private onPrev = () => {
    if (!this.gallery.length) return;
    this.galleryIndex = (this.galleryIndex - 1 + this.gallery.length) % this.gallery.length;
  };
  private onNext = () => {
    if (!this.gallery.length) return;
    this.galleryIndex = (this.galleryIndex + 1) % this.gallery.length;
  };

  // + Current image getter
  private currentImage() {
    return this.gallery[this.galleryIndex] || null;
  }

  render() {
    if (this.loading) {
      return html`
        <main class="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-slate-100">
          <div class="max-w-6xl mx-auto px-4 py-10">
            <div class="h-8 w-48 bg-slate-800 rounded animate-pulse mb-4"></div>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              ${Array.from({ length: 8 }).map(() => this.skeletonCard())}
            </div>
          </div>
        </main>
      `;
    }

    if (this.error) {
      return html`
        <main class="min-h-screen bg-slate-950 text-slate-100">
          <div class="max-w-3xl mx-auto px-4 py-12">
            <div class="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              ${this.error}
            </div>
          </div>
        </main>
      `;
    }

    const d = this.d!;
    const currentImage = this.currentImage

    return html`
      <main class="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-slate-100">
        <div class="max-w-6xl mx-auto px-4 py-8 sm:py-12">

          <!-- Header (name + chips + Edit) -->
          <current-diffsona .slug=${this.slug}></current-diffsona>

          <!-- Primary actions -->
          <div class="mt-4 mb-6 flex flex-wrap items-center gap-2">
            <a
              href="/diffsona/${encodeURIComponent(this.slug)}/create"
              class="rounded-xl border border-indigo-400/30 bg-indigo-500/20 hover:bg-indigo-500/30 px-3 py-2 text-sm font-medium"
            >✨ Create Content</a>

            <!--<button
              class="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-2 text-sm"
              @click=${() => (this.showUpload = !this.showUpload)}
            >➕ Add Media</button>

            <button
              class="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-2 text-sm disabled:opacity-50"
              ?disabled=${this.working}
              @click=${this.onRetrain}
            >🔄 Retrain</button>-->

            <!--<div class="ml-auto flex items-center gap-2">
              ${this.tab('🎨 Created Content', 'content')}
              ${this.tab('🖼 Source Media', 'source')}
            </div>-->
          </div>

          <!-- Upload area -->
          ${this.showUpload ? html`
            <div class="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
              <upload-media-form
                .slug=${this.slug}
                @media-added=${() => this.loadDiffsona()}
              ></upload-media-form>
              <p class="mt-2 text-xs text-slate-400">Tip: Add a handful of strong, varied photos.</p>
            </div>
          ` : null}

          <!-- Content -->
          ${this.activeTab === 'source' ? html`
            <section class="space-y-8">
              <div>
                <h3 class="text-sm font-semibold text-slate-200 mb-2">Primary Images</h3>
                <media-grid
                  .mediaIds=${d.primary_media_ids}
                  .slug=${this.slug}
                  .isPrimary=${true}
                  @media-removed=${() => this.loadDiffsona()}
                ></media-grid>
              </div>

              <div>
                <h3 class="text-sm font-semibold text-slate-200 mb-2">Secondary Images</h3>
                <media-grid
                  .mediaIds=${d.secondary_media_ids}
                  .slug=${this.slug}
                  .isPrimary=${false}
                  @media-removed=${() => this.loadDiffsona()}
                ></media-grid>
              </div>
            </section>
          ` : html`
            <section>
              ${
                (!this.loading && this.images.length === 0)
                  ? html`
                    <!-- First-time empty state CTA -->
                    <div
                      class="rounded-2xl border border-white/10 bg-white/[0.04] p-8 sm:p-10 text-center
                             flex flex-col items-center justify-center gap-4"
                    >
                      <div class="text-4xl">✨</div>
                      <h3 class="text-xl sm:text-2xl font-semibold text-slate-100">
                        Ready to make your first look?
                      </h3>
                      <p class="text-slate-300 max-w-md">
                        Generate images from your Diffsona using simple, delightful prompts.
                      </p>
                      <a
                        href="/diffsona/${encodeURIComponent(this.slug)}/create"
                        class="inline-flex items-center gap-2 rounded-xl border border-indigo-400/30
                               bg-indigo-500/20 hover:bg-indigo-500/30 px-5 py-2.5 font-medium"
                      >
                        Create content
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path fill-rule="evenodd"
                                d="M4.5 12a.75.75 0 0 1 .75-.75h11.69l-4.72-4.72a.75.75 0 1 1 1.06-1.06l6 6a.75.75 0 0 1 0 1.06l-6 6a.75.75 0 1 1-1.06-1.06l4.72-4.72H5.25A.75.75 0 0 1 4.5 12Z"
                                clip-rule="evenodd"/>
                        </svg>
                      </a>
                    </div>
                  `
                  : null
              }

              <!-- Always keep the grid below so once images exist (or when scrolled),
                   it loads/appears seamlessly -->
              <infinite-image-grid
                .items=${this.images}
                .fetchPage=${this.makeFetcher()}
                .autoLoad=${this.images.length === 0}
                .shareCta=${true}
                @open=${this.openFromGrid}
              ></infinite-image-grid>
            </section>
          `}
        </div>

        <!-- Share / Viewer Modal with gallery navigation -->
        <share-modal
          .open=${this.shareOpen}
          .image=${currentImage}
          .imageKey=${null}
          .showNav=${true}
          @close=${this.closeShare}
          @prev=${this.onPrev}
          @next=${this.onNext}
        ></share-modal>
      </main>
    `;
  }
}

